import { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { supabase } from '../supabaseClient';
import { useAuth } from './AuthContext';

const WishlistContext = createContext(null);

// Tracks which product_ids the signed-in customer has saved, so the heart
// button on every card can render its state without each one hitting the DB.
// Wishlist is a logged-in feature — guests get bounced to /login by the button.
export function WishlistProvider({ children }) {
  const { user } = useAuth();
  const [ids, setIds] = useState(() => new Set());
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    if (!user) { setIds(new Set()); setLoading(false); return; }
    const { data } = await supabase
      .from('wishlist_items')
      .select('product_id')
      .eq('user_id', user.id);
    setIds(new Set((data || []).map((r) => r.product_id)));
    setLoading(false);
  }, [user]);

  useEffect(() => { setLoading(true); refresh(); }, [refresh]);

  // Add a product to the wishlist. Pass the current price so we have a baseline
  // for the future price-drop notifier. Returns true on success.
  const add = useCallback(async (product) => {
    if (!user) return false;
    const { error } = await supabase.from('wishlist_items').insert({
      user_id: user.id,
      product_id: product.id,
      price_cents_at_add: product.price_cents ?? null,
    });
    // 23505 = unique violation (already saved) — treat as a no-op success.
    if (error && error.code !== '23505') return false;
    setIds((prev) => new Set(prev).add(product.id));
    return true;
  }, [user]);

  const remove = useCallback(async (productId) => {
    if (!user) return false;
    const { error } = await supabase
      .from('wishlist_items')
      .delete()
      .eq('user_id', user.id)
      .eq('product_id', productId);
    if (error) return false;
    setIds((prev) => { const next = new Set(prev); next.delete(productId); return next; });
    return true;
  }, [user]);

  const has = useCallback((productId) => ids.has(productId), [ids]);

  return (
    <WishlistContext.Provider value={{ ids, loading, has, add, remove, refresh }}>
      {children}
    </WishlistContext.Provider>
  );
}

export function useWishlist() {
  const ctx = useContext(WishlistContext);
  if (!ctx) throw new Error('useWishlist must be used within WishlistProvider');
  return ctx;
}
